import { buildPaginationRequestParams } from '../../services/pagination';
import { setMembers } from '../members/actions';
import { getPageType, getMemberIds, getCursor, getTotalMembersCount } from './selectors';
import { PAGE_TYPE } from './constants';
import * as Navigation from '../navigation/actions';
import { closeModal } from '../../actions/close-modal';
import { getFFPageDemoMembers } from '../../constants/demo-data';
import { MEMBERS_PER_PAGE } from '../../constants/pagination';
import { translate } from '../../services/i18n';
import { VIEW_MODE_EDITOR } from '../../constants/view-modes';
import { getViewedMember } from '../members/selectors';
import { rolesActionClicked, customRolesActionsMap, openRoleModal } from '../roles/actions';
import { getIsDemoMode, getIsMobile, getViewMode, isExperimentEnabled } from '../site/selectors';
import { Experiments } from '../../constants/experiments';

const urijs = require('urijs');

export const SET_TOTAL_MEMBERS_COUNT = 'ffPage/SET_TOTAL_MEMBERS_COUNT';
export const SET_MEMBER_IDS = 'ffPage/SET_MEMBER_IDS';
export const SET_IS_LOADING = 'ffPage/SET_IS_LOADING';
export const SET_CURSOR = 'ffPage/SET_CURSOR';
export const SET_HAS_NEXT = 'ffPage/SET_HAS_NEXT';

const setTotalMembersCount = (count) => ({
  type: SET_TOTAL_MEMBERS_COUNT,
  payload: count,
});
export const setMemberIds = (memberIds) => ({
  type: SET_MEMBER_IDS,
  payload: memberIds,
});
const setIsLoading = (isLoading) => ({
  type: SET_IS_LOADING,
  payload: isLoading,
});
const setCursor = (cursor) => ({ type: SET_CURSOR, payload: cursor });
const setHasNext = (hasNext) => ({ type: SET_HAS_NEXT, payload: hasNext });

const buildUri = (uid, pageType, page) => {
  const path = `/_api/members/${uid}/${pageType === PAGE_TYPE.FOLLOWERS ? 'followers' : 'following'}`;
  const query = buildPaginationRequestParams(page, MEMBERS_PER_PAGE);
  return urijs(path).query(query);
};

const buildFollowersV3Uri = (uid, pageType, cursor) => {
  const path = `/_api/members/${uid}/${pageType === PAGE_TYPE.FOLLOWERS ? 'followers' : 'following'}`;
  const query = { cursor };

  return urijs(path).query(query);
};

const COUNT_HEADER = 'wix-forum-totalresults';

const parseMembersCount = (headers) => parseInt(headers[COUNT_HEADER], 10);

export const exitModal = () => (dispatch) => dispatch(closeModal());

const getMembersOrDemoMembers = (viewMode, body = [], headers = {}) => {
  if (viewMode === VIEW_MODE_EDITOR && body.length === 0) {
    const demoMembers = getFFPageDemoMembers(translate);
    return { members: demoMembers, totalCount: demoMembers.length };
  }

  return { members: body, totalCount: parseMembersCount(headers) };
};

const getMembersOrDemoMembersV3 = (viewMode, body = {}) => {
  if (viewMode === VIEW_MODE_EDITOR && body.length === 0) {
    const demoMembers = getFFPageDemoMembers(translate);
    return { members: demoMembers };
  }

  return { members: body.members, cursor: body.cursor, hasNext: body.hasNext };
};

const updateMembersData = (members, totalCount, cursor, hasNext) => (dispatch, getState) => {
  // To do: create actions which adds and not overwrites
  const state = getState();
  const currentMembers = Object.values(state.members.map);
  const currentFFIds = state.ffPage.memberIds;

  dispatch(setMembers([...currentMembers, ...members]));
  dispatch(setMemberIds([...currentFFIds, ...members.map((m) => m.uid)]));
  dispatch(setTotalMembersCount(totalCount));

  if (cursor) {
    dispatch(setCursor(cursor));
  }

  if (hasNext !== undefined) {
    dispatch(setHasNext(hasNext));
  }
};

export const fetchMembers = (page = 1) => (dispatch, getState, { request }) => {
  const state = getState();
  const pageType = getPageType(state);
  const viewMode = getViewMode(state);
  const viewedMember = getViewedMember(state);
  const currentCursor = getCursor(state);
  const totalCountV3 = getTotalMembersCount(state);
  const isFollowersV3Enabled = isExperimentEnabled(state, Experiments.USE_FOLLOWERS_V3);

  if (isFollowersV3Enabled) {
    return Promise.resolve()
      .then(() => dispatch(setIsLoading(true)))
      .then(() =>
        request(buildFollowersV3Uri(viewedMember.uid, pageType, currentCursor), {
          parseHeaders: true,
        }),
      )
      .then(({ body }) => getMembersOrDemoMembersV3(viewMode, body))
      .then(({ members, cursor, hasNext }) => dispatch(updateMembersData(members, totalCountV3, cursor, hasNext)))
      .then(() => dispatch(setIsLoading(false)));
  }

  return Promise.resolve()
    .then(() => dispatch(setIsLoading(true)))
    .then(() =>
      request(buildUri(viewedMember.uid, pageType, page), {
        parseHeaders: true,
      }),
    )
    .then(({ body, headers }) => getMembersOrDemoMembers(viewMode, body, headers))
    .then(({ members, totalCount }) => dispatch(updateMembersData(members, totalCount)))
    .then(() => dispatch(setIsLoading(false)));
};

export const renewFFMembers = () => (dispatch) => {
  dispatch(setMemberIds([]));
  dispatch(setTotalMembersCount(0));
  return dispatch(fetchMembers());
};

export const removeFollowerFromList = (uid) => (dispatch, getState) => {
  const ids = getMemberIds(getState());
  return dispatch(setMemberIds(ids.filter((a) => a !== uid)));
};

// TODO: where are tests??
export const goToFFPage = (...props) => (dispatch, getState) => {
  if (getIsMobile(getState())) {
    dispatch(Navigation.goToMemberPage(...props));
  } else {
    // TO DO: Fix fetching of viewed member
    dispatch(Navigation.goToFFPage(...props));
  }
};

// TODO: where are tests??
export const goToMemberPage = (...props) => (dispatch, getState) => {
  if (getIsDemoMode(getState())) {
    return;
  }

  dispatch(Navigation.goToMemberPage(...props));
};

export const goToMembersPage = () => (dispatch, getState) => {
  dispatch(Navigation.goToMembersPage());
  if (getIsMobile(getState())) {
    dispatch(exitModal());
  }
};

const onDeleteMember = (memberId) => (dispatch) => {
  dispatch(removeFollowerFromList(memberId));
};

export const onRoleAction = (memberId, role) => (dispatch) => {
  dispatch(rolesActionClicked(memberId, role.roleId));

  if (customRolesActionsMap[role.roleId]) {
    return dispatch(customRolesActionsMap[role.roleId]({ memberId, onDeleteMember }));
  }

  return dispatch(openRoleModal(memberId, role));
};
